import { useTheme, type Theme } from '@mui/material';
import { useMatches } from '@tanstack/react-router';
import { useLayoutEffect, useRef } from 'react';
import { devToolsNavigationItem, navigationSections } from '../../routes/navigation';

import puffinFavicon from '../../../public/favicon.svg?raw';

export const createFaviconUrl = (icon: string | undefined, palette: Theme['palette']): string => {
  const primary = palette.primary.main;
  const background = palette.background.default;
  const svg = icon
    ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect width="24" height="24" rx="3" fill="${primary}"/><path transform="translate(1 1) scale(.916667)" fill="${background}" d="${icon}"/></svg>`
    : puffinFavicon.replace(/color="[^"]+"/, `color="${background}"`).replace(/(<rect[^>]*fill=")[^"]+"/, `$1${primary}"`);
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export const DocumentFavicon = () => {
  const faviconLink = useRef<HTMLLinkElement | null>(null);
  const palette = useTheme().palette;
  const activePath = useMatches({
    select: (matches) => [...matches].reverse().find((match) => match.staticData.navigationPath)?.staticData.navigationPath,
  });
  const icon = [...navigationSections.flatMap((section) => section.items), devToolsNavigationItem].find(
    (item) => 'path' in item && item.path === activePath,
  )?.icon;

  useLayoutEffect(() => {
    const existingLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const link = existingLink ?? document.createElement('link');
    const originalHref = link.getAttribute('href');
    const originalType = link.getAttribute('type');
    link.rel = 'icon';
    faviconLink.current = link;
    if (!existingLink) document.head.append(link);

    return () => {
      faviconLink.current = null;
      if (!existingLink) {
        link.remove();
        return;
      }
      if (originalHref === null) link.removeAttribute('href');
      else link.setAttribute('href', originalHref);
      if (originalType === null) link.removeAttribute('type');
      else link.setAttribute('type', originalType);
    };
  }, []);

  useLayoutEffect(() => {
    const link = faviconLink.current;
    if (link) {
      link.type = 'image/svg+xml';
      link.href = createFaviconUrl(icon, palette);
    }
  }, [icon, palette]);

  return null;
};
